import { LoaderCircle, MessageSquareOff } from 'lucide-react';
import { useEffect } from 'react';
import { Link, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import { GatewayActions, GatewayBrand, GatewayContent, GatewayShell } from '@/_shared/components/GatewayShell';
import { Button } from '@/_shared/components/ui/button';

import { WorkspaceProvider } from '../contexts/WorkspaceContext';
import { useAuth } from '../hooks/useAuth';
import { setLastWorkspaceId } from '../lib/last-workspace';
import { isWorkspaceChatPath } from '../lib/workspace-chat-path';
import { AnalyticsWorkspaceGroup } from './AnalyticsWorkspaceGroup';
import { LoginRedirect } from './LoginRedirect';
import { RudderstackAccessDenied } from './RudderstackAccessDenied';

/**
 * ACT2-1225: signed-in user opened a chat deep link for a workspace they cannot access.
 * Do not mount the chat route (no chat fetch). Login redirects and other workspace denials stay elsewhere.
 */
function ChatUnavailable(): React.ReactElement {
  return (
    <GatewayShell>
      <GatewayBrand poweredBy />
      <GatewayContent
        icon={<MessageSquareOff />}
        variant="warning"
        title="Chat unavailable"
        description="You need access to this Lookout workspace to open the chat. The workspace may no longer exist."
      />
      <GatewayActions>
        <Button variant="primary" size="lg" className="w-full" asChild>
          <Link to="/" state={{ pickWorkspace: true }}>
            Choose a workspace
          </Link>
        </Button>
      </GatewayActions>
    </GatewayShell>
  );
}

/**
 * Wrapper for /w/:slug/* routes. Verifies the user is a member of the workspace.
 * Workspace context flows from the URL — no JWT workspace binding needed.
 */
export function WorkspaceRoute(): React.ReactElement {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const { user, workspaces, loading } = useAuth();

  const ws = slug ? workspaces.find((w) => w.id === slug) : undefined;

  // Remember the last valid workspace the user opened so `/` can re-enter it next time.
  useEffect(() => {
    if (ws && !ws.rudderstackAccessDenied) setLastWorkspaceId(ws.id);
  }, [ws]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return <LoginRedirect />;

  if (!slug || !ws) {
    // Chat deep links get a dedicated denial (ACT2-1225) so a Slack URL for a foreign
    // workspace does not bounce through the picker with an opaque redirect.
    if (isWorkspaceChatPath(location.pathname)) {
      return <ChatUnavailable />;
    }
    // Unknown / non-member workspace: bounce to home, preserving the sub-path
    // (e.g. /w/badslug/settings/sources → pick a valid workspace → /w/:valid/settings/sources).
    const rest = `${location.pathname.replace(/^\/w\/[^/]+/, '')}${location.search}`;
    return <Navigate to="/" replace state={{ from: rest }} />;
  }

  // A sibling workspace that the RudderStack membership check denies shows why (ACT2-1124 §5.7).
  if (ws.rudderstackAccessDenied) {
    return (
      <RudderstackAccessDenied
        deniedSlugs={workspaces.filter((w) => w.rudderstackAccessDenied).map((w) => w.id)}
        denial={ws.rudderstackAccessDenied}
        canOpenAnother={workspaces.some((w) => !w.rudderstackAccessDenied)}
      />
    );
  }

  return (
    <WorkspaceProvider key={slug} slug={slug} workspace={ws}>
      <AnalyticsWorkspaceGroup />
      <Outlet />
    </WorkspaceProvider>
  );
}
